/**
 * BgcStatusBadge — single source of truth for the Background Check
 * verification badge. Surfaces consistent visuals on profile, discovery
 * card, and DM headers.
 *
 * Severity hierarchy:
 *   verified  → small green ShieldCheck pill (always show)
 *   pending   → amber Clock pill (show only on owner's surfaces)
 *   failed    → muted ShieldOff pill (show only on owner's surfaces)
 *   none      → render nothing
 */
import { ShieldCheck, ShieldOff, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

export type BgcStatus = "verified" | "pending" | "failed" | "none";

interface BgcStatusBadgeProps {
  status: BgcStatus;
  /** Render only when status === "verified" — use on public surfaces. */
  publicOnly?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const VARIANTS: Record<
  Exclude<BgcStatus, "none">,
  { label: string; className: string; Icon: typeof ShieldCheck }
> = {
  verified: {
    label: "Background verified",
    className:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    Icon: ShieldCheck,
  },
  pending: {
    label: "Background check pending",
    className:
      "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    Icon: Clock,
  },
  failed: {
    label: "Background check failed",
    className:
      "bg-muted text-muted-foreground border-border",
    Icon: ShieldOff,
  },
};

const BgcStatusBadge = ({
  status,
  publicOnly,
  size = "sm",
  className,
}: BgcStatusBadgeProps) => {
  if (status === "none") return null;
  if (publicOnly && status !== "verified") return null;

  const v = VARIANTS[status];
  const sizing =
    size === "md"
      ? "px-2.5 py-1 text-xs gap-1.5"
      : "px-2 py-0.5 text-[10px] gap-1";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border font-medium",
        sizing,
        v.className,
        className,
      )}
      aria-label={v.label}
      title={v.label}
    >
      <v.Icon className={size === "md" ? "h-3.5 w-3.5" : "h-3 w-3"} aria-hidden />
      {size === "md" ? v.label : v.label.replace("Background ", "")}
    </span>
  );
};

export default BgcStatusBadge;

/** Helpers to coerce profile fields into a status. */
export const bgcStatusFromProfile = (p: {
  bgc_verified?: boolean | null;
  bgc_status?: string | null;
} | null | undefined): BgcStatus => {
  if (!p) return "none";
  if (p.bgc_verified) return "verified";
  const s = (p.bgc_status ?? "").toLowerCase();
  if (s === "pending" || s === "in_progress" || s === "submitted") return "pending";
  if (s === "failed" || s === "rejected") return "failed";
  return "none";
};
